import { SportService } from "@prestosports/prestostats-core/Services";

/**
 * The definitions for a season or event's settings, scoped to its ruleset and
 * grouped by the section that draws them (PSTATS-1267).
 *
 * Kept a plain function rather than a hook so it can be unit tested in this
 * repo's existing style; callers memoize it.
 *
 * A season or event that never had a ruleset picked stores no ruleSetId and
 * runs on the sport's default. resolveCustomFormData returns {} for an
 * undefined one, which empties the list and would silently hide every
 * definition rather than failing visibly.
 */
const resolveRuleSetId = (sportId, ruleSetId) =>
	ruleSetId || (sportId ? SportService.getDefaultRuleSetId(sportId) : null);

/**
 * The name of the rule set whose settings are actually being shown.
 *
 * Resolved the same way the definitions are, including the fall back to the
 * sport's default - otherwise a season that never had one picked would have
 * its dialog name one rule set while listing another's settings.
 *
 * Taken straight from the sport map rather than from the sport context's
 * ruleSetOptions: only Event Settings and Season Settings ever populate that,
 * so the Game Start panel had nothing to look a name up in and would have
 * shown none at all.
 */
export const resolveRuleSetName = (sportId, ruleSetId) => {
	const resolved = resolveRuleSetId(sportId, ruleSetId);

	if (!sportId || !resolved) {
		return undefined;
	}

	return SportService.getRuleSetOptions(sportId)?.find(
		(option) => option.id === resolved
	)?.name;
};

export const resolveSettingDefinitions = (
	sportId,
	ruleSetId,
	gameConfiguration
) => {
	const resolvedRuleSetId = resolveRuleSetId(sportId, ruleSetId);

	if (!sportId || !resolvedRuleSetId || !gameConfiguration) {
		return [];
	}

	return SportService.getSettingDefinitions(
		sportId,
		resolvedRuleSetId,
		gameConfiguration
	);
};

/** The entries for one section, or [] when that section defines none. */
export const entriesForSection = (definitions, section) =>
	definitions?.find((group) => group.section === section)?.entries || [];
